import { Box, Card, CardActions, CardMedia, styled } from '@mui/material';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DownloadExtensionButton from './DownloadExtensionButton';
import RemoveExtensionButton from './RemoveExtensionButton';
import extPlaceholder from '../../../assets/ext-placeholder.png';
import { buildPath, PRIVATE_ROUTE } from '../../../routes/routes';
import { currencyFormat } from '../../../utils/paymentUtils';
import { formatExtensionType } from '../../../utils/utils';
import Text from '../../elements/text/Text';
import Badge from '../../sections/chips/Badge';

const StyledCard = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 300,
});

const StyledCardMedia = styled(CardMedia)({
  height: 125,
  width: 125,
  objectFit: 'contain',
  alignSelf: 'center',
});

const StyledCardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  maxHeight: 230,
  overflowY: 'auto',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  [theme.breakpoints.down('sm')]: {
    maxHeight: 250,
    flexDirection: 'column-reverse',
  },
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const StyledText = styled(Text)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  padding: '0.25em 0.75em',
  borderRadius: '5px',
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    marginBottom: '0.5em',
    width: '50%',
    padding: '0.25em',
  },
}));

const StyledActionButtonWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  '& > button': {
    marginLeft: '1em',
    [theme.breakpoints.down('sm')]: {
      marginLeft: '-0.5em',
      marginTop: '0.5em',
    },
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
  },
}));

const ExtensionCard = ({
  data,
  isLibrary,
  handleAdd,
  handleDelete,
  ...restProps
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDetailPageLink = () =>
    navigate(
      buildPath({
        path: PRIVATE_ROUTE.EXTENSION_DETAILS,
        params: { extensionId: data.id },
      })
    );

  const extensionType = formatExtensionType(data.type);

  return (
    <StyledCard variant="outlined" aria-label="extension card">
      <Box sx={{ display: 'flex', flexDirection: 'column', p: '0.5em' }}>
        <StyledCardHeader>
          <StyledCardMedia
            image={data.icon || extPlaceholder}
            title={data.name}
            aria-label="extension icon"
            component="img"
          />
          <Box sx={{ m: 1, alignItems: 'center' }}>
            <Badge
              label={extensionType.display}
              color={extensionType.className}
              aria-label="extension type"
            />
            <Text aria-label="extension name">{data.name}</Text>
          </Box>
        </StyledCardHeader>
        <Box sx={{ mt: 1, ml: 0.25 }}>
          <Text
            caption
            component="p"
            color="textSecondary"
            aria-label="extension developer"
          >
            {`${t('developer', { ns: 'fields' })}: ${data.developerName}`}
          </Text>
          {data.license && (
            <Text
              caption
              component="p"
              color="textSecondary"
              aria-label="extension license"
            >
              {`${t('license', { ns: 'fields' })}: ${data.license}`}
            </Text>
          )}
          {data.copyright && (
            <Text
              caption
              component="p"
              color="textSecondary"
              aria-label="extension copyright"
            >
              {`${t('copyright', { ns: 'fields' })}: ${data.copyright}`}
            </Text>
          )}
        </Box>
      </Box>
      <StyledCardActions>
        <StyledText component="div" aria-label="extension price">
          {data.free
            ? t('billing.free', { ns: 'project' })
            : currencyFormat(data.price)}
        </StyledText>
        <StyledActionButtonWrapper>
          <Button
            variant="text"
            size="small"
            onClick={handleDetailPageLink}
            aria-label="view extension detail"
          >
            {t('details', { ns: 'buttons' })}
          </Button>
          {!isLibrary && (
            <>
              <DownloadExtensionButton
                extension={data}
                onAddExtension={handleAdd}
                {...restProps}
              />
              <RemoveExtensionButton extension={data} onDelete={handleDelete} />
            </>
          )}
        </StyledActionButtonWrapper>
      </StyledCardActions>
    </StyledCard>
  );
};

ExtensionCard.propTypes = {
  data: PropTypes.object.isRequired,
  isLibrary: PropTypes.bool,
  handleAdd: PropTypes.func,
  handleDelete: PropTypes.func,
};

export default ExtensionCard;
